import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import placeOrderForApproval from "@dana/Overrides/Store/Pages/OnePageCheckout/Handlers/PlaceOrderForApproval";
import setIsPlacingOrder from "@dana/Overrides/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCurrentAccount from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateCurrentAccount";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    termsAndConditionsLink = "termsAndConditionsLink",
    privacyPolicyLink = "privacyPolicyLink",

}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsAndConditionsLink]: LinkFieldValue;
        [fields.privacyPolicyLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const cartMode = getCookie('cartMode');
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartMode === 'EmergencyOrderCart'
        ? (state.emergencyCartState.cart as Cart) ?? { properties: {} } as Cart
        : cartMode === 'StockOrderCart'
            ? cartState.value
            : state.annualReturnsCartState.cart as Cart ?? { properties: {} } as Cart;
    const { isLoading } = cartState;
    const promotions = getCurrentPromotionsDataView(state).value;
    return {
        cart,
        account: getCurrentAccountState(state).value,
        billTo: cartMode === 'StockOrderCart'
            ? getBillToState(state, cartState.value?.billToId).value
            : cartMode === 'EmergencyOrderCart'
                ? state.emergencyCartState.cart?.billTo
                : state.annualReturnsCartState.cart?.billTo,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading,
        isExpanded: true,
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        placeOrderErrorMessage: state.pages.onePageCheckout.placeOrderErrorMessage,        
        showPlaceOrderButton: canPlaceOrder(cart),
        showSubmitForApprovalOrderButton: canSubmitForApprovalOrder(cart),
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
        cartMode,
        orderNotes: state.onePageCheckoutStateExt?.editableNotes,
        carrierAccountNumber: state.onePageCheckoutStateExt?.carrierAccountNumber,
        isEmea: useIsEmea(state),
         priceChangeModaIsOn: state.orderProgressMode.isOn
    };
};

const mapDispatchToProps = {
    setIsPlacingOrder,
    placeOrderForApproval,
    updateCurrentAccount,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OnePageCheckoutReviewAndPlaceOrderStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    checkboxesGridItem?: GridItemProps;
    signUpForNewslettersCheckbox?: CheckboxPresentationProps;
    placeOrderGridItem?: GridItemProps;
    placeOrderButton?: ButtonPresentationProps;
    downloadButton?: ButtonPresentationProps;
    actionButton?: LinkPresentationProps;
    agreementText?: TypographyPresentationProps;
    placeOrderErrorMessageGridItem?: GridItemProps;
    placeOrderErrorMessageText?: TypographyPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
}

export const reviewAndPlaceOrderStyles: OnePageCheckoutReviewAndPlaceOrderStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 120px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    cartTotalGridItem: { width: 12 },
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
    checkboxesGridItem: { width: 12 },
    placeOrderGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    placeOrderButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    downloadButton: {},
    actionButton: {},
    agreementText: {
        size: 13,
        css: css`
            margin-top: 8px;
        `,
    },
    placeOrderErrorMessageGridItem: { width: 12 },
    placeOrderErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    submitForApprovalButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = reviewAndPlaceOrderStyles;

const OnePageCheckoutReviewAndPlaceOrder = ({
    fields,
    cart,
    account,
    billTo,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    isExpanded,
    isPlacingOrder,
    placeOrderErrorMessage,
    showPlaceOrderButton,
    showSubmitForApprovalOrderButton,
    orderApprovalDetailsPageUrl,
    history,
    cartMode,
    orderNotes,
    setIsPlacingOrder,
    placeOrderForApproval,
    updateCurrentAccount,
    carrierAccountNumber,
    isEmea,
    priceChangeModaIsOn
}: Props) => {
    const [signUpForNewsletters, setSignUpForNewslettersCheckbox] = useState(false);
    const { url: termsAndConditionsUrl } = useGetLink(fields.termsAndConditionsLink);
    const { url: privacyPolicyUrl } = useGetLink(fields.privacyPolicyLink);
    const [validaAccountno, setValidAccountNo] = useState(false);
    const [validDisposition, setValidDisposition] = useState(false);
    const [validcarrierselect, setValidcarrierselect] = useState(false);
    const [validShipVia, setValidShipVia] = useState(false);   
    useEffect(() => {
        const input = document.querySelector('[data-test-selector="textcarrierAccountNumber"]') as HTMLInputElement | null;
        if (input) {
            if (input.value.trim() === "") {
                setValidAccountNo(true);
            } else {
                setValidAccountNo(false);
            }
        } else {
            setValidAccountNo(false);
        }
        const carrierselect = document.querySelector('[data-test-selector="onepagecheckout_carrierSelect"]') as HTMLSelectElement | null;
        const shipviaselect = document.querySelector('[data-test-selector="onepagecheckout_shippingServiceSelect"]') as HTMLSelectElement | null;
        if (carrierselect && carrierselect.value.trim() === "") {
            carrierselect.focus();
            setValidcarrierselect(true);
        }else{
            setValidcarrierselect(false);
        }
        if (shipviaselect && shipviaselect.value.trim() === "") {
            shipviaselect.focus();
             setValidShipVia(true);
        }else{
            setValidShipVia(false);
        }
    }, [cart?.carrier, cart?.shipVia, carrierAccountNumber]);

    useEffect(() => {
        if (cartMode !== "AnnualReturnsOrderCart") {
            setValidDisposition(true);
        } else {
            const decision = orderNotes;
            if (decision && decision !== "") {
                setValidDisposition(true);
            } else {
                setValidDisposition(false);
            }
        }
    }, [orderNotes, cartMode]);

    if (!cart || isPlacingOrder) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_reviewAndPlaceOrder" />
            </StyledWrapper>
        );
    }

    const emailForSubsribe = account?.isGuest ? billTo?.email : account?.email;

    const isZeroProductInCart = () => {
        if (isEmea === true) {
            if (cart?.orderGrandTotal === 0) {
                return true;
            }
            const id = (cart?.cartLines || [])
                .find(c => Number(c?.pricing?.extendedUnitNetPrice || 0) === 0)
                ?.productId ?? null;
            if (id !== null) {
                return true;
            }
            return false;
        }
        return false;
    };

    const runFieldValidation = (): boolean => {        
        const po = document.querySelector('[data-test-selector="onePageCheckout_poNumber"]') as HTMLInputElement | null;
        if (po && po.value.trim() === "") {
            po.focus();
            return false;
        }       
        const input = document.querySelector('[data-test-selector="textcarrierAccountNumber"]') as HTMLInputElement | null;
        const carrierselect = document.querySelector('[data-test-selector="onepagecheckout_carrierSelect"]') as HTMLSelectElement | null;
        const shipviaselect = document.querySelector('[data-test-selector="onepagecheckout_shippingServiceSelect"]') as HTMLSelectElement | null;
        if (carrierselect && carrierselect.value.trim() === "") {
            carrierselect.focus();
            return false;
        }
        if (shipviaselect && shipviaselect.value.trim() === "") {
            shipviaselect.focus();
            return false;
        }
        if (input && input.value.trim() === "") {
            input.focus();
            return false;
        }
        return true;
    };
    const isOrderValidation = () => {    
        if (typeof window === 'undefined' || typeof document === 'undefined') {
         return false; // Default safe fallback for SSR
         }    
        const po = document.querySelector('[data-test-selector="onePageCheckout_poNumber"]') as HTMLInputElement | null;
        if (po && po.value.trim() === "") {
            po.focus();
            return true;
        }       
        const input = document.querySelector('[data-test-selector="textcarrierAccountNumber"]') as HTMLInputElement | null;
        const carrierselect = document.querySelector('[data-test-selector="onepagecheckout_carrierSelect"]') as HTMLSelectElement | null;
        const shipviaselect = document.querySelector('[data-test-selector="onepagecheckout_shippingServiceSelect"]') as HTMLSelectElement | null;
        if (carrierselect && carrierselect.value.trim() === "") {
            carrierselect.focus();
            return true;
        }
        if (shipviaselect && shipviaselect.value.trim() === "") {
            shipviaselect.focus();
            return true;
        }
        if (input && input.value.trim() === "") {
            input.focus();
            return true;
        }
        return false;
    };

    const placeOrderClickHandler = () => {
        if (!runFieldValidation()) {
            return;
        }
        setIsPlacingOrder({ isPlacingOrder: true });
    };

    const submitForApprovalOrderClickHandler = () => {
        placeOrderForApproval({
            onSuccess: (cartId: string) => {
                if (orderApprovalDetailsPageUrl) {
                    history.push(`${orderApprovalDetailsPageUrl}?cartId=${cartId}`);
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart.id) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
        });
    };

    return (
        <GridContainer {...styles.container} data-test-selector="onePageCheckoutReviewAndPlaceOrder_header">
            <GridItem {...styles.titleGridItem}>
                <Typography as="h2" {...styles.titleText}>
                    {cart?.status === "Cart"
                        ? translate("Stock")
                        : cart?.status === "AnnualReturnsCart"
                            ? translate("Annual Returns")
                            : translate("Emergency")} {translate("Order Summary")}
                </Typography>
            </GridItem>
            {isExpanded && (
                <>
                    <GridItem {...styles.cartTotalGridItem}>
                        <CartTotalDisplay
                            isLoading={isLoading || priceChangeModaIsOn}
                            cart={cart}
                            orderPromotions={orderPromotions}
                            shippingPromotions={shippingPromotions}
                            discountTotal={discountTotal}
                            extendedStyles={styles.cartTotal}
                        />
                    </GridItem>


                    <GridItem {...styles.placeOrderGridItem}>
                        {showPlaceOrderButton && (
                            <>
                                <Button
                                    {...styles.placeOrderButton}
                                    disabled={validShipVia || validcarrierselect || isZeroProductInCart() || isLoading || isPlacingOrder || validaAccountno || !validDisposition}
                                    onClick={placeOrderClickHandler}
                                    data-test-selector="checkoutReviewAndSubmit_placeOrder"
                                >
                                    {translate("Place Order")}
                                </Button>

                            </>
                        )}
                        {showSubmitForApprovalOrderButton && (
                            <Button
                                {...styles.submitForApprovalButton}
                                disabled={isLoading || isPlacingOrder}
                                onClick={submitForApprovalOrderClickHandler}
                                data-test-selector="checkoutReviewAndSubmit_submitForApproval"
                            >
                                {translate("Submit For Approval")}
                            </Button>
                        )}
                        {showPlaceOrderButton && termsAndConditionsUrl && privacyPolicyUrl && (
                            <Typography {...styles.agreementText}>
                                {siteMessage("Checkout_Agreement", termsAndConditionsUrl, privacyPolicyUrl)}
                            </Typography>
                        )}
                        <>
                            <Link className="btn btn-secondary" href="/" {...styles.actionButton}>
                                {translate("Continue Shopping")}
                            </Link>
                            {cartMode === "AnnualReturnsOrderCart" ? (
                                <Link className="btn btn-secondary" href="/cart?IsAnnualReturnsOrder=true" {...styles.actionButton}>
                                    {translate("Review Order")}
                                </Link>
                            ) : (
                                <Link className="btn btn-secondary" href="/cart" {...styles.actionButton}>
                                    {translate("Go To Cart")}
                                </Link>
                            )}

                        </>
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutReviewAndPlaceOrder)),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "Review & Place Order",
        fieldDefinitions: [
            {
                name: fields.termsAndConditionsLink,
                fieldType: "Contextual",
                displayName: "Terms And Conditions Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.privacyPolicyLink,
                fieldType: "Contextual",
                displayName: "Privacy Policy Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            }

        ],
    },
};

export default widgetModule;
